import { useAuth } from '@clerk/clerk-react'
import useSWR from 'swr'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

export function StatisticsPage() {
  const { getToken } = useAuth()

  const { data: stats, error, isLoading } = useSWR(
    '/api/v1/statistics',
    async (url) => {
      const token = await getToken()
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      return res.json()
    }
  )

  if (isLoading) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">読み込み中...</p>
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="text-center py-20">
        <p className="text-red-500">エラーが発生しました</p>
      </div>
    )
  }

  return (
    <div className="space-y-8 animate-slide-up">
      <h2 className="text-3xl font-bold">視聴統計</h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="border border-border rounded-xl p-6 bg-gradient-to-br from-card to-card/50 shadow-lg">
          <p className="text-sm text-muted-foreground mb-2">総作品数</p>
          <p className="text-4xl font-bold netflix-gradient bg-clip-text text-transparent">{stats.totalTitles}</p>
        </div>
        <div className="border border-border rounded-xl p-6 bg-gradient-to-br from-card to-card/50 shadow-lg">
          <p className="text-sm text-muted-foreground mb-2">総視聴回数</p>
          <p className="text-4xl font-bold netflix-gradient bg-clip-text text-transparent">{stats.totalViews}</p>
        </div>
        <div className="border border-border rounded-xl p-6 bg-gradient-to-br from-card to-card/50 shadow-lg">
          <p className="text-sm text-muted-foreground mb-2">平均視聴回数</p>
          <p className="text-4xl font-bold netflix-gradient bg-clip-text text-transparent">{stats.avgViews.toFixed(1)}</p>
        </div>
      </div>

      {/* Top Genres Chart */}
      <div className="border border-border rounded-xl p-6 bg-card shadow-lg">
        <h3 className="text-xl font-semibold mb-4">人気ジャンル</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={stats.topGenres}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="genre" angle={-45} textAnchor="end" height={100} interval={0} stroke="hsl(var(--foreground))" />
            <YAxis stroke="hsl(var(--foreground))" />
            <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
            <Legend />
            <Bar dataKey="count" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly Viewing Chart */}
      <div className="border border-border rounded-xl p-6 bg-card shadow-lg">
        <h3 className="text-xl font-semibold mb-4">月別視聴数</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={stats.viewingByMonth}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="month" stroke="hsl(var(--foreground))" />
            <YAxis stroke="hsl(var(--foreground))" />
            <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
            <Legend />
            <Bar dataKey="count" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Titles */}
      <div className="border border-border rounded-xl p-6 bg-card shadow-lg">
        <h3 className="text-xl font-semibold mb-4">最も視聴した作品</h3>
        <div className="space-y-3">
          {stats.topTitles.map((title: any, index: number) => (
            <div key={title.id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
              <span className="text-3xl font-bold netflix-gradient bg-clip-text text-transparent w-10">
                {index + 1}
              </span>
              <div className="flex-1">
                <p className="font-semibold text-lg">{title.name}</p>
                <p className="text-sm text-muted-foreground">
                  {title.viewCount}回視聴
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
